import { AuthService, SessionInfo } from "@iotos/ims-auth-sdk/auth";

import { API_ENDPOINT } from "../../config/api";

import { ImsAppUser } from "./interfaces/user";

/**
 * Маршруты BFF. `loginPath` задан явно и намеренно совпадает с `signIn`:
 * `redirectToLogin()` строит `${baseUrl}${loginPath}`, а дефолт SDK — `/login`,
 * то есть редирект ушёл бы на `/api/login`, который отвечает 401 вместо 302 на IMS.
 */
const IMS_ROUTES = {
  signIn: "/ims/sign-in",
  user: "/ims/me",
  signOut: "/ims/sign-out",
  loginPath: "/ims/sign-in",
} as const;

/** Проверка сессии не удалась по причине, отличной от «сессии нет». */
export class SessionCheckError extends Error {
  /** HTTP-статус `/ims/me`; `0` — ответа не было (сеть, таймаут, CORS). */
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "SessionCheckError";
    this.status = status;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

const isImsUser = (data: unknown): data is ImsAppUser =>
  typeof data === "object" &&
  data !== null &&
  "isAuthenticated" in data &&
  ("userId" in data || "user_id" in data || "subject" in data);

/**
 * TODO(IMS-SDK): обёртка закрывает три места SDK, которые мешают показать пользователю
 * понятное состояние. Убрать, когда это будет исправлено в самом SDK:
 * - `initialize()` кеширует отклонённый промис, и повторная проверка после сбоя
 *   возвращает тот же отказ без запроса;
 * - любой ответ, кроме 401, SDK превращает в безликий `Error` — 403 не отличить
 *   от сетевого сбоя;
 * - успешный JSON принимается за пользователя без проверки.
 */
class EkocropAuthService extends AuthService<ImsAppUser> {
  async initialize(): Promise<SessionInfo<ImsAppUser>> {
    try {
      return await super.initialize();
    } catch (error) {
      this.initializationPromise = null;
      throw error;
    }
  }

  protected async loadCurrentUser(): Promise<void> {
    let response: Response;
    try {
      response = await this.config.fetchClient(
        `${this.config.baseUrl}${this.routes.user}`,
        {
          credentials: "include",
        }
      );
    } catch {
      this.clearAuthState();
      throw new SessionCheckError(0, "Сервер авторизации недоступен");
    }

    if (response.status === 401) {
      this.clearAuthState();
      return;
    }

    if (!response.ok) {
      this.clearAuthState();
      throw new SessionCheckError(
        response.status,
        `Проверка сессии: HTTP ${response.status}`
      );
    }

    const data: unknown = await response.json().catch(() => null);
    if (!isImsUser(data)) {
      this.clearAuthState();
      throw new SessionCheckError(
        response.status,
        "Проверка сессии: ответ не похож на пользователя"
      );
    }

    this.setAuthState(data);
    this.extractCsrfToken();
  }

  /**
   * Выход с возвратом на страницу фронтенда. BFF принимает только локальный `returnUrl`
   * и склеивает его с `SignedOutRedirectUri` из своей конфигурации.
   */
  async signOutTo(returnPath: string): Promise<void> {
    this.clearAuthState();
    const returnUrl = encodeURIComponent(returnPath);
    this.config.redirectHandler(
      `${this.config.baseUrl}${this.routes.signOut}?returnUrl=${returnUrl}`
    );
  }
}

export const imsAuthService = new EkocropAuthService({
  baseUrl: API_ENDPOINT,
  routes: IMS_ROUTES,
});
